import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoginForm from '../components/Auth/LoginForm';
import RegisterForm from '../components/Auth/RegisterForm';
import OTPVerification from '../components/Auth/OTPVerification';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import authService from '../services/authService';
import logo from '../assets/logo.png';

const AuthPage = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [registrationEmail, setRegistrationEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (formData) => {
    setIsLoading(true);
    setError('');

    try {
      await login(formData.email, formData.password);
      setSuccessMessage('Login successful! Redirecting...');
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (formData) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await authService.register(formData);
      setRegistrationEmail(formData.email);
      setShowOTPVerification(true);
      setSuccessMessage('Registration successful! Please check your email for the verification code.');
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (otp) => {
    setIsLoading(true);
    setError('');

    try {
      await authService.verifyEmail(registrationEmail, otp);
      setSuccessMessage('Email verified successfully! Redirecting...');
      
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);
    } catch (err) {
      setError(err.message || 'Invalid or expired verification code.');
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError('');
    setSuccessMessage('');

    try {
      await authService.resendOTP(registrationEmail);
      setSuccessMessage('Verification code sent! Please check your email.');
    } catch (err) {
      setError(err.message || 'Failed to resend code. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F8FA] to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-lg w-full"
      >
        {/* Success Message */}
        <AnimatePresence>
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4"
            >
              <p className="text-sm text-green-700">{successMessage}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Auth Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src={logo} alt="Notapaths Logo" className="h-12" />
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => {
                setActiveTab('login');
                setError('');
                setSuccessMessage('');
              }}
              className={`flex-1 py-2.5 px-4 rounded-md font-medium transition-all ${
                activeTab === 'login'
                  ? 'bg-white text-[#202356] shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => {
                setActiveTab('register');
                setError('');
                setSuccessMessage('');
              }}
              className={`flex-1 py-2.5 px-4 rounded-md font-medium transition-all ${
                activeTab === 'register'
                  ? 'bg-white text-[#202356] shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Forms */}
          <AnimatePresence mode="wait">
            {showOTPVerification ? (
              <motion.div
                key="otp"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
              >
                <OTPVerification
                  email={registrationEmail}
                  onVerify={handleVerifyOTP}
                  onResend={handleResendOTP}
                  isLoading={isLoading}
                  error={error}
                />
              </motion.div>
            ) : activeTab === 'login' ? (
              <motion.div
                key="login"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                <LoginForm 
                  onSubmit={handleLogin} 
                  isLoading={isLoading} 
                  error={error}
                  onForgotPassword={() => setShowForgotPassword(true)}
                />
              </motion.div>
            ) : (
              <motion.div
                key="register"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <RegisterForm onSubmit={handleRegister} isLoading={isLoading} error={error} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Footer Links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-6 text-center text-sm text-gray-600"
        >
          <p>
            By continuing, you agree to our{' '}
            <a href="/terms-of-service" className="text-[#202356] hover:text-[#151b3d] font-medium">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy-policy" className="text-[#202356] hover:text-[#151b3d] font-medium">
              Privacy Policy
            </a>
          </p>
        </motion.div>
      </motion.div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal 
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </div>
  );
};

export default AuthPage;
